import { Button, Select, Segmented, Space, Typography } from 'antd';
import { ClearOutlined, PlusOutlined } from '@ant-design/icons';
import type { CatalogoResponse, UsuarioPagoRef } from '@aid/shared';
import { DateRangeFilter, type DateRangeValue } from '../../../components/DateRangeFilter';
import { ClienteSelect } from '../../../components/ClienteSelect';
import type { PagosTableGroupBy, PagosTranslate } from './pago-table-types';
import { PAGOS_TABLE_GROUP } from './pago-table-types';

const { Text } = Typography;

export interface PagosTableToolbarProps {
  t: PagosTranslate;
  showClientFilter: boolean;
  showUserFilter?: boolean;
  showGrouping: boolean;
  groupBy: PagosTableGroupBy;
  idCliente: string;
  idEstadoPago?: number;
  idTipoPago?: number;
  idUsuario?: string;
  userOptions?: UsuarioPagoRef[];
  fechaDesde?: string;
  fechaHasta?: string;
  estadosPago: CatalogoResponse[];
  tiposPago: CatalogoResponse[];
  canCreate: boolean;
  hasFilters: boolean;
  onGroupByChange: (value: PagosTableGroupBy) => void;
  onClientChange: (value: string) => void;
  onEstadoChange: (value: number | undefined) => void;
  onTipoChange: (value: number | undefined) => void;
  onUserChange?: (value: string | undefined) => void;
  onDateRangeChange: (value: DateRangeValue | null) => void;
  onClear: () => void;
  onCreate: () => void;
}

export function PagosTableToolbar({
  t,
  showClientFilter,
  showUserFilter = false,
  showGrouping,
  groupBy,
  idCliente,
  idEstadoPago,
  idTipoPago,
  idUsuario,
  userOptions = [],
  fechaDesde,
  fechaHasta,
  estadosPago,
  tiposPago,
  canCreate,
  hasFilters,
  onGroupByChange,
  onClientChange,
  onEstadoChange,
  onTipoChange,
  onUserChange,
  onDateRangeChange,
  onClear,
  onCreate,
}: PagosTableToolbarProps) {
  const handleUserChange = (value: string | undefined) => onUserChange?.(value);
  const dateRange: DateRangeValue | null =
    fechaDesde !== undefined || fechaHasta !== undefined
      ? [fechaDesde ?? null, fechaHasta ?? null]
      : null;
  const clienteId =
    idCliente !== '' && Number.isFinite(Number(idCliente)) ? Number(idCliente) : undefined;

  // Client-side search across username + full name: the option label is JSX,
  // so the default string match would miss it — map the option value (user
  // id) to the searchable text and match case-insensitively.
  const userSearchText = new Map(
    userOptions.map((user) => [user.id, `${user.username} ${user.nombre}`.toLowerCase()]),
  );

  return (
    <div
      style={{
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
      }}
    >
      <Space wrap size={[8, 8]} className="table-filters">
        {showGrouping && (
          <>
            <Text type="secondary">{t('common.groupBy')}</Text>
            <Segmented
              options={[
                { label: t('common.groupByNone'), value: PAGOS_TABLE_GROUP.NONE },
                { label: t('common.groupByCliente'), value: PAGOS_TABLE_GROUP.CLIENTE },
                { label: t('common.groupByEstado'), value: PAGOS_TABLE_GROUP.ESTADO },
              ]}
              value={groupBy}
              onChange={(value) => onGroupByChange(value as PagosTableGroupBy)}
            />
          </>
        )}
        {showClientFilter && (
          <div style={{ width: 220 }}>
            <ClienteSelect
              value={clienteId}
              onChange={(id) => onClientChange(id === undefined ? '' : String(id))}
              placeholder={t('pages.pagos.searchPlaceholder')}
            />
          </div>
        )}
        <Select<number>
          placeholder={t('pages.pagos.filtroEstado')}
          allowClear
          style={{ width: 180 }}
          value={idEstadoPago}
          onChange={onEstadoChange}
          options={estadosPago.map((estado) => ({
            label: estado.value ?? estado.key ?? `#${estado.id}`,
            value: estado.id,
          }))}
        />
        <Select<number>
          placeholder={t('pages.pagos.filtroTipo')}
          allowClear
          style={{ width: 180 }}
          value={idTipoPago}
          onChange={onTipoChange}
          options={tiposPago.map((tipo) => ({
            label: tipo.value ?? tipo.key ?? `#${tipo.id}`,
            value: tipo.id,
          }))}
        />
        {showUserFilter && (
          <Select<string>
            placeholder={t('pages.pagos.filtroUsuario')}
            allowClear
            showSearch
            style={{ width: 240 }}
            data-testid="user-filter-select"
            value={idUsuario}
            onChange={handleUserChange}
            filterOption={(input, option) => {
              const term = input.trim().toLowerCase();
              if (term === '' || typeof option?.value !== 'string') return true;
              return userSearchText.get(option.value)?.includes(term) ?? false;
            }}
            options={userOptions.map((user) => {
              return {
                value: user.id,
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                    <span>{user.username}</span>
                    {user.nombre !== '' ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {user.nombre}
                      </Text>
                    ) : null}
                  </span>
                ),
              };
            })}
          />
        )}
        <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />
        {hasFilters && (
          <Button
            type="link"
            icon={<ClearOutlined />}
            aria-label={t('common.clear')}
            onClick={onClear}
          >
            {t('common.clear')}
          </Button>
        )}
      </Space>
      {canCreate && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          aria-label={t('pages.pagos.create')}
          onClick={onCreate}
        >
          {t('pages.pagos.create')}
        </Button>
      )}
    </div>
  );
}
